import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vuestic-ui'
import api, {
  login,
  verify2fa,
  createOrganization as apiCreateOrganization,
  updateUserProfile as apiUpdateUserProfile,
  OrganizationCreatePayload,
  UserProfileUpdatePayload,
} from '../services/api'
import { Role } from '../pages/roles/types'
import { getUserGroupsByProject, getUserGroupsInProject } from '../services/api/permission-groups'

// interface LoginCredentials {
//   email: string
//   password: string
// }

// interface TokenResponse {
//   access_token: string
//   token_type: string
//   has_organization: boolean
// }

export const useAuthStore = defineStore('auth', () => {
  const { init } = useToast()

  const token = ref<string | null>(localStorage.getItem('token'))
  const user = ref<any>(null)
  const requires2fa = ref(false)
  const pendingCredentials = ref<{ email: string; password: string } | null>(null)
  const hasOrganization = ref<boolean | null>(null)
  const organizationId = ref<number | null>(null)
  const isOwner = ref<boolean>(false)
  const isInitialized = ref<boolean>(false)
  const userProjectRoles = ref<Record<number, Role[]>>({}) // User roles cache in projects
  const userPermissionGroups = ref<Record<number, any[]>>({}) // User permission groups cache by project
  const availableProjects = ref<number[]>([]) // Projects user has access to
  const projectNameById = ref<Record<number, string>>({}) // Cache for project names
  /** True after a successful batch loadUserPermissions for the current session user. */
  const projectPermissionsLoaded = ref(false)
  /** Projects whose cache was cleared and must be re-fetched from API. */
  const staleProjectIds = ref<Set<number>>(new Set())

  const isAuthenticated = computed(() => !!token.value)

  // Getters for user permissions
  const getUserRolesForProject = (projectId: number): Role[] => {
    return userProjectRoles.value[projectId] || []
  }

  const getUserProjects = (): number[] => {
    return availableProjects.value
  }

  const hasProjectAccess = (projectId: number): boolean => {
    return availableProjects.value.includes(projectId)
  }

  const getUserPermissionGroupsForProject = (projectId: number): any[] => {
    return userPermissionGroups.value[projectId] || []
  }

  // Get cached project name by id
  const getProjectName = (projectId: number): string | undefined => {
    return projectNameById.value[projectId]
  }

  // Helper function to check project ownership for the current user's organization
  const _isProjectInUsersOrganization = async (projectId: number): Promise<boolean> => {
    // Only owners within an organization can be checked this way
    if (!isOwner.value || !organizationId.value) {
      return false
    }

    const orgId = organizationId.value

    try {
      // Используем правильный эндпоинт с ID организации
      const projectResponse = await api.get(`/org/${orgId}/odk-projects/${projectId}`)

      // Ensure the project data contains organization_id before comparison
      // Дополнительно проверяем, что ID организации совпадает
      return projectResponse.data && projectResponse.data.organization_id === orgId
    } catch (error) {
      console.error(`[Auth Store] Failed to check if project ${projectId} belongs to organization ${orgId}:`, error)
      return false // Return false in case of error
    }
  }

  const setToken = (newToken: string | null, orgStatus?: boolean) => {
    token.value = newToken
    if (newToken) {
      localStorage.setItem('token', newToken)
      if (orgStatus !== undefined) {
        hasOrganization.value = orgStatus
        localStorage.setItem('hasOrganization', String(orgStatus))
      } else {
        const storedStatus = localStorage.getItem('hasOrganization')
        hasOrganization.value = storedStatus ? storedStatus === 'true' : null
      }
    } else {
      localStorage.removeItem('token')
      localStorage.removeItem('hasOrganization')
      hasOrganization.value = null
    }
  }

  const initAuth = async () => {
    if (isInitialized.value) {
      return
    }

    const storedToken = localStorage.getItem('token')
    const storedOrgStatus = localStorage.getItem('hasOrganization')

    if (storedToken) {
      setToken(storedToken, storedOrgStatus ? storedOrgStatus === 'true' : undefined)

      try {
        await loadUser() // Make sure we await this
      } catch (error) {
        console.error('[Auth Store] Failed to initialize auth:', error)
        // Clear invalid token
        setToken(null)
        user.value = null
        organizationId.value = null
        localStorage.removeItem('organizationId')
      }
    }

    // Always mark as initialized, regardless of success or failure
    isInitialized.value = true

    return {
      isAuthenticated: isAuthenticated.value,
      hasOrganization: hasOrganization.value,
      organizationId: organizationId.value,
      isOwner: isOwner.value,
    }
  }

  const loginUser = async (email: string, password: string) => {
    try {
      const response = await login(email, password)
      const loginPayload = response.data as {
        requires_2fa?: boolean
        requires2fa?: boolean
        message?: string
        access_token?: string
      }

      if (loginPayload.requires_2fa === true || loginPayload.requires2fa === true) {
        requires2fa.value = true
        pendingCredentials.value = { email, password }
        return { success: false, requires2fa: true, hasOrganization: null }
      }

      const orgStatus = (response.data as { has_organization?: boolean }).has_organization ?? null
      setToken(loginPayload.access_token ?? null, orgStatus ?? undefined)
      await loadUser()
      return { success: true, requires2fa: false, hasOrganization: orgStatus }
    } catch (error: any) {
      init({
        message: error.response?.data?.detail || 'Login failed',
        color: 'danger',
      })
      return { success: false, requires2fa: false, hasOrganization: null }
    }
  }

  const verify2faCode = async (code: string) => {
    if (!pendingCredentials.value) {
      init({
        message: 'No pending login attempt',
        color: 'danger',
      })
      return { success: false, hasOrganization: null, hasOrgId: false }
    }

    try {
      const response = await verify2fa(pendingCredentials.value.email, pendingCredentials.value.password, code)

      const orgStatus = response.data.has_organization
      setToken(response.data.access_token, orgStatus)
      requires2fa.value = false
      pendingCredentials.value = null
      await loadUser()

      // Ensure organizationId is available after loadUser
      const hasOrgId = organizationId.value !== null

      return { success: true, hasOrganization: orgStatus, hasOrgId }
    } catch (error: any) {
      init({
        message: error.response?.data?.detail || 'Verification failed',
        color: 'danger',
      })
      return { success: false, hasOrganization: null, hasOrgId: false }
    }
  }

  const loadUser = async () => {
    if (!token.value) {
      return
    }
    try {
      const response = await api.get('/users/me')
      user.value = response.data
      organizationId.value = response.data.organization_id ?? null

      // Обеспечиваем синхронизацию значения isOwner с user.is_owner
      const userIsOwner = response.data.is_owner === true // Явное сравнение с true для безопасности

      // Устанавливаем значение isOwner после проверки
      isOwner.value = userIsOwner

      const orgExists = organizationId.value !== null
      hasOrganization.value = orgExists
      localStorage.setItem('hasOrganization', String(orgExists))

      if (organizationId.value !== null) {
        localStorage.setItem('organizationId', String(organizationId.value))
      } else {
        localStorage.removeItem('organizationId')
      }

      // Load user's project-based permissions
      await loadUserPermissions()
    } catch (error) {
      console.error('[Auth Store] Failed to load user:', error)
      logout()
    }
  }

  const applyProjectGroups = (projectId: number, projectGroups: any[]) => {
    const projectRoles: string[] = []
    projectGroups.forEach((group) => {
      if (group.group_roles) {
        group.group_roles.forEach((role: { role_name?: string }) => {
          if (role.role_name && !projectRoles.includes(role.role_name)) {
            projectRoles.push(role.role_name)
          }
        })
      }
    })

    userPermissionGroups.value[projectId] = projectGroups
    userProjectRoles.value[projectId] = projectRoles.map((roleName) => ({ name: roleName }) as Role)

    if (projectRoles.length > 0) {
      if (!availableProjects.value.includes(projectId)) {
        availableProjects.value.push(projectId)
      }
    } else {
      availableProjects.value = availableProjects.value.filter((id) => id !== projectId)
    }

    const nextStale = new Set(staleProjectIds.value)
    nextStale.delete(projectId)
    staleProjectIds.value = nextStale
  }

  /**
   * Hydrate permissions for one project from API (used after cache clear / cold miss).
   * Keeps per-project maps as SSOT — never merges roles across projects.
   */
  const hydrateProjectPermissions = async (projectId: number): Promise<void> => {
    if (!user.value?.id || !projectId) return
    try {
      const projectGroups = await getUserGroupsInProject(user.value.id, projectId)
      applyProjectGroups(projectId, projectGroups || [])
    } catch (error) {
      console.warn(`[Auth Store] Failed to hydrate groups for project ${projectId}:`, error)
      applyProjectGroups(projectId, [])
    }
  }

  const loadUserPermissions = async () => {
    if (!user.value?.id) {
      return
    }

    try {
      // Reset caches before repopulating
      userProjectRoles.value = {}
      userPermissionGroups.value = {}
      availableProjects.value = []
      projectPermissionsLoaded.value = false
      staleProjectIds.value = new Set()
      // NOTE: We intentionally do NOT cache any "global" roles/groups under a synthetic project ID (e.g., 0)
      // because the application model uses only project-based roles. This avoids confusion in the UI and logic.

      // One batch request replaces: getOrganizationProjects + N× getUserGroupsInProject
      const projectsWithGroups = await getUserGroupsByProject(user.value.id)

      for (const entry of projectsWithGroups) {
        const projectId = Number(entry.project_id)
        if (!projectId) continue

        if (entry.project_name) {
          projectNameById.value[projectId] = String(entry.project_name)
        }

        applyProjectGroups(projectId, entry.groups || [])
      }

      projectPermissionsLoaded.value = true
    } catch (error) {
      console.error('[Auth Store] Failed to load user permissions:', error)
      projectPermissionsLoaded.value = false
    }
  }

  const updateUserProfile = async (payload: UserProfileUpdatePayload) => {
    if (!isAuthenticated.value) {
      init({ message: 'You must be logged in to update your profile.', color: 'warning' })
      return false
    }
    try {
      const response = await apiUpdateUserProfile(payload)
      user.value = response.data
      return true
    } catch (error: any) {
      init({ message: error.response?.data?.detail || 'Failed to update profile', color: 'danger' })
      return false
    }
  }

  const logout = (redirectToLogin = true) => {
    setToken(null)
    user.value = null
    organizationId.value = null
    localStorage.removeItem('organizationId')
    requires2fa.value = false
    pendingCredentials.value = null
    userProjectRoles.value = {}
    userPermissionGroups.value = {}
    availableProjects.value = []
    projectPermissionsLoaded.value = false
    staleProjectIds.value = new Set()

    // Only redirect to login if explicitly requested (not during initialization).
    // Lazy-import router to avoid circular deps (router imports this store). Use replace
    // so the protected screen is not left in browser history.
    if (redirectToLogin) {
      void import('../router').then(({ default: routerInstance }) => {
        void routerInstance.replace({ name: 'login' }).catch(() => {
          // Ignore duplicate navigation / concurrent redirects
        })
      })
    }
  }

  const createOrganization = async (payload: OrganizationCreatePayload) => {
    if (!user.value?.id) {
      init({ message: 'User not loaded, cannot create organization.', color: 'danger' })
      return false
    }
    try {
      // Добавляем user_id в payload если его ещё нет
      const orgPayload = {
        ...payload,
        user_id: payload.user_id || user.value.id,
      }

      const response = await apiCreateOrganization(orgPayload)

      // Обновляем состояние хранилища с данными новой организации
      const newOrganization = response.data
      organizationId.value = newOrganization.id
      hasOrganization.value = true
      isOwner.value = true

      // Сохраняем в localStorage
      localStorage.setItem('hasOrganization', 'true')
      localStorage.setItem('organizationId', String(newOrganization.id))

      // CRITICAL: Reload user data from server to get updated is_owner status
      await loadUser()

      init({ message: 'Organization created successfully!', color: 'success' })
      return true
    } catch (error: any) {
      init({ message: error.response?.data?.detail || 'Failed to create organization', color: 'danger' })
      return false
    }
  }

  const resetPassword = async (payload: { token: string; newPassword: string }) => {
    try {
      const response = await api.post('/auth/reset-password', payload)
      return response.data
    } catch (_) {
      throw new Error('Failed to reset password')
    }
  }

  // Functions for working with user roles in projects

  // Get the roles of the current user in the project (per-project SSOT map in this store)
  const getUserRolesInProject = async (projectId: number): Promise<Role[]> => {
    if (!user.value?.id) {
      return []
    }

    try {
      const needsHydrate =
        staleProjectIds.value.has(projectId) ||
        (!Object.prototype.hasOwnProperty.call(userProjectRoles.value, projectId) && !projectPermissionsLoaded.value)

      if (needsHydrate) {
        await hydrateProjectPermissions(projectId)
      }

      return userProjectRoles.value[projectId] || []
    } catch (error) {
      console.error(`[Auth Store] Failed to get user roles for project ${projectId}:`, error)
      return []
    }
  }

  // Check if the current user has the specified role in the project
  const hasRoleInProject = async (projectId: number, roleName: string): Promise<boolean> => {
    if (!user.value?.id) {
      return false
    }

    try {
      // If the user is a superuser, return true immediately
      if (user.value.is_superuser) {
        return true
      }

      // Get the roles of the user
      const roles = await getUserRolesInProject(projectId)

      // Check if the user has the specified role
      return roles.some((role) => role.name === roleName)
    } catch (error) {
      console.error(`[Auth Store] Failed to check role ${roleName} for project ${projectId}:`, error)
      return false
    }
  }

  // Check if the current user has any of the specified roles in the project
  const hasAnyRoleInProject = async (projectId: number, roleNames: string[]): Promise<boolean> => {
    if (!user.value?.id) {
      return false
    }

    try {
      // If the user is a superuser, return true immediately
      if (user.value.is_superuser) {
        return true
      }

      // If the user is the owner and the role 'manager' is among the checked
      if (isOwner.value && roleNames.includes('manager')) {
        // Check if the project belongs to the user's organization using the helper function
        if (await _isProjectInUsersOrganization(projectId)) {
          return true
        }
      }

      // Get the roles of the user
      const roles = await getUserRolesInProject(projectId)

      // Check if the user has at least one of the specified roles
      return roles.some((role) => roleNames.includes(role.name))
    } catch (error) {
      console.error(`[Auth Store] Failed to check roles ${roleNames.join(', ')} for project ${projectId}:`, error)
      return false
    }
  }

  // Check if the current user has access to view data in the project
  const hasDataAccessInProject = async (projectId: number): Promise<boolean> => {
    return hasAnyRoleInProject(projectId, ['manager', 'data_viewer', 'data_downloader'])
  }

  // Check if the current user has access to edit data in the project
  const hasDataEditAccessInProject = async (projectId: number): Promise<boolean> => {
    return hasAnyRoleInProject(projectId, ['manager', 'data_collector'])
  }

  // Check if the current user is a project manager
  const isProjectManager = async (projectId: number): Promise<boolean> => {
    // If the user is a superuser, return true immediately
    if (user.value?.is_superuser) {
      return true
    }

    // If the user is the owner, check if the project belongs to their organization
    if (isOwner.value) {
      // Use the helper function here
      if (await _isProjectInUsersOrganization(projectId)) {
        return true
      }
    }

    // Check if the user has the 'manager' role
    return hasRoleInProject(projectId, 'manager')
  }

  // Clear the cache for the specified project (marks stale so next read re-fetches that project only)
  const clearProjectRolesCache = (projectId: number) => {
    if (Object.prototype.hasOwnProperty.call(userProjectRoles.value, projectId)) {
      delete userProjectRoles.value[projectId]
    }
    if (Object.prototype.hasOwnProperty.call(userPermissionGroups.value, projectId)) {
      delete userPermissionGroups.value[projectId]
    }
    const nextStale = new Set(staleProjectIds.value)
    nextStale.add(projectId)
    staleProjectIds.value = nextStale
  }

  // Clear the entire roles cache
  const clearAllProjectRolesCache = () => {
    userProjectRoles.value = {}
    userPermissionGroups.value = {}
    availableProjects.value = []
    projectPermissionsLoaded.value = false
    staleProjectIds.value = new Set()
  }

  /** Force re-fetch groups/roles for one project into the SSOT map. */
  const refreshProjectPermissions = async (projectId: number): Promise<Role[]> => {
    clearProjectRolesCache(projectId)
    await hydrateProjectPermissions(projectId)
    return userProjectRoles.value[projectId] || []
  }

  return {
    token,
    user,
    isAuthenticated,
    requires2fa,
    pendingCredentials,
    hasOrganization,
    organizationId,
    isOwner,
    isInitialized,
    userProjectRoles,
    userPermissionGroups,
    availableProjects,
    setToken,
    initAuth,
    loginUser,
    verify2faCode,
    loadUser,
    loadUserPermissions,
    updateUserProfile,
    logout,
    createOrganization,
    resetPassword,

    // New functions for working with user roles in projects
    getUserRolesInProject,
    hasRoleInProject,
    hasAnyRoleInProject,
    hasDataAccessInProject,
    hasDataEditAccessInProject,
    isProjectManager,
    clearProjectRolesCache,
    clearAllProjectRolesCache,
    refreshProjectPermissions,
    hydrateProjectPermissions,
    projectPermissionsLoaded,

    // New permission getters
    getUserRolesForProject,
    getUserProjects,
    hasProjectAccess,
    getUserPermissionGroupsForProject,
    // Project name getter
    getProjectName,
    projectNameById,
  }
})
